// src/utils/api/requestCache.ts

import { CACHE_CONFIG } from './constants';
import type { CacheEntry } from '../../types/quoter/types';

class RequestCache {
    private cache: Map<string, CacheEntry<any>>;
    private cleanupInterval: NodeJS.Timeout;

    constructor() {
        this.cache = new Map();
        // Run cleanup periodically
        this.cleanupInterval = setInterval(
            () => this.cleanup(),
            CACHE_CONFIG.CLEANUP_INTERVAL
        );
    }

    private cleanup(): void {
        const now = Date.now();
        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                this.cache.delete(key);
            }
        }
    }

    public set<T>(key: string, data: T, ttl: number = CACHE_CONFIG.DEFAULT_TTL): void {
        // Enforce maximum entries limit
        if (this.cache.size >= CACHE_CONFIG.MAX_ENTRIES) {
            // Get oldest entry
            const oldestEntry = Array.from(this.cache.entries())
                .sort(([, a], [, b]) => a.timestamp - b.timestamp)[0];
            if (oldestEntry) {
                this.cache.delete(oldestEntry[0]);
            }
        }

        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl
        });
    }

    public get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) {
            return null;
        }

        // Check if entry has expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }

        return entry.data;
    }

    public has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) {
            return false;
        }

        // Check if entry has expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    public delete(key: string): void {
        this.cache.delete(key);
    }

    public clear(): void {
        this.cache.clear();
    }

    public size(): number {
        return this.cache.size;
    }

    public keys(): string[] {
        return Array.from(this.cache.keys());
    }

    public destroy(): void {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
        }
        this.clear();
    }

    // Helper method to generate consistent cache keys
    public generateKey(parts: (string | number | boolean | undefined)[]): string {
        return parts
            .filter(part => part !== undefined)
            .map(part => String(part))
            .join(':');
    }
}

// Export a singleton instance
export const requestCache = new RequestCache();